/**
 * "137 notes" channel renderer (channel_kind = `notes_137`).
 *
 * Read-only timeline of dictation notes captured by the 137 callback pipeline:
 * the owner calls the Hub number, hub-cell-edge records until hangup, and
 * hub-people transcribes + stores the note (table `voice_notes`). This channel
 * lists those notes newest-first — each row shows the transcript, the
 * originating number, the capture time, and an inline audio player for the
 * stored WAV.
 *
 * Backend contract (handlers/calls.rs):
 *   GET /api/voice-notes?server_id=...   -> { notes: VoiceNote[] }  (newest first)
 *   GET /api/voice-notes/:id/audio       -> stored WAV (session-gated)
 *
 * The note-taking *config* (enabled / owner / prompt / tone / max_secs) lives in
 * the Calls > Automation panel (channels/calls-automation.ts), not here.
 */

import { get, API_BASE } from '../api';
import { state } from '../state';
import { fmtCallTime, fmtFull } from '../util/time';
import { realtime, channelTopic, type RealtimeEvent } from '../realtime';

interface VoiceNote {
  id: string;
  from: string;
  transcript: string;
  has_audio: boolean;
  created_at: string;
}

let notes: VoiceNote[] = [];
let currentTopic: string | null = null;
let unsubRealtime: (() => void) | null = null;

export function cleanupNotes137(): void {
  if (unsubRealtime) { unsubRealtime(); unsubRealtime = null; }
  if (currentTopic) { realtime.unsubscribeChannel(currentTopic); currentTopic = null; }
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

/** Render the list + count from the module `notes` array. */
function renderList(listEl: HTMLElement, countEl: HTMLElement | null): void {
  if (countEl) countEl.textContent = `${notes.length} note${notes.length === 1 ? '' : 's'}`;

  if (notes.length === 0) {
    listEl.innerHTML = `
      <div style="padding:48px 20px;text-align:center;color:var(--fg-muted);font-family:var(--font-sans)">
        <div style="font-size:18px;margin-bottom:8px">No notes yet</div>
        <div style="font-size:13px">Call your Hub number — it records what you say and the transcript appears here.</div>
      </div>
    `;
    return;
  }

  // Backend already returns newest-first (ORDER BY created_at DESC); keep a
  // defensive client sort so a row order change server-side can't surprise us.
  const sorted = [...notes].sort((a, b) => b.created_at.localeCompare(a.created_at));
  listEl.innerHTML = sorted.map(renderRow).join('');
}

export async function renderNotes137(container: HTMLElement): Promise<void> {
  cleanupNotes137();
  notes = [];

  const sid = state.currentServerId ?? state.currentServer;
  if (!sid) {
    container.innerHTML = '<div style="padding:20px;color:var(--fg-muted)">Select a server to view notes.</div>';
    return;
  }

  container.innerHTML = `
    <div style="flex:1;min-height:0;display:flex;flex-direction:column">
      <div style="padding:10px 16px;border-bottom:1px solid var(--border);display:flex;gap:10px;align-items:center;font-family:var(--font-sans);font-size:13px">
        <div style="font-weight:600;color:var(--fg)">137 notes</div>
        <div id="notes137-count" style="color:var(--fg-muted)">loading…</div>
      </div>
      <div id="notes137-list" style="flex:1;min-height:0;overflow-y:auto"></div>
    </div>
  `;

  const listEl = container.querySelector<HTMLElement>('#notes137-list');
  const countEl = container.querySelector<HTMLElement>('#notes137-count');
  if (!listEl) return;

  try {
    const res = await get<{ notes: VoiceNote[] }>(`/api/voice-notes?server_id=${encodeURIComponent(sid)}`);
    notes = res.notes ?? [];
  } catch {
    notes = [];
  }

  renderList(listEl, countEl);

  // Live updates — a note captured while this channel is open is prepended
  // without a refetch. Mirrors the #cell-status live channel.
  const topic = channelTopic(sid, '137-notes');
  currentTopic = topic;
  realtime.connect();
  realtime.subscribeChannel(topic);
  unsubRealtime = realtime.on((ev: RealtimeEvent) => {
    if (ev.type !== 'voice_note_created' || ev.channel_id !== topic) return;
    const n = ev.note as unknown as VoiceNote;
    if (notes.some(x => x.id === n.id)) return; // de-dup vs the initial fetch
    notes.unshift(n);                           // newest first
    renderList(listEl, countEl);
  });
}

function renderRow(n: VoiceNote): string {
  const when = fmtCallTime(n.created_at);
  const whenFull = fmtFull(n.created_at);
  const from = (n.from || '').trim();
  const transcript = (n.transcript || '').trim();
  const audio = n.has_audio
    ? `<audio controls preload="none" src="${API_BASE}/api/voice-notes/${esc(n.id)}/audio" style="margin-top:8px;width:100%;max-width:420px;height:36px"></audio>`
    : '';
  return `
    <div class="notes137-row" data-id="${esc(n.id)}" style="padding:14px 16px;border-bottom:1px solid var(--border);font-family:var(--font-sans)">
      <div style="display:flex;align-items:baseline;gap:8px;color:var(--fg-muted);font-size:12px">
        ${from ? `<span style="font-family:var(--font-mono,monospace);color:var(--fg)">${esc(from)}</span>` : '<span>Unknown number</span>'}
        <span title="${esc(whenFull)}">${esc(when)}</span>
      </div>
      <div style="margin-top:4px;font-size:14px;color:var(--fg);white-space:pre-wrap;line-height:1.45">${esc(transcript || '(no transcript captured)')}</div>
      ${audio}
    </div>
  `;
}
